import { useEffect, useState } from 'react'
import { Typography, Table, Space, Modal, Button, Form, Select, Row, Col } from 'antd'
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, PlusOutlined, UserOutlined, MailOutlined, PhoneOutlined, LockOutlined, GlobalOutlined, TagsOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getAndSearchEmployee, deleteSingleEmployee, createEmployee, updateSingleEmployee } from '../actions/employeeAction'
import { CustomButton } from '../components/ui/CustomButton'
import { CustomInput } from '../components/ui/CustomInput'
import { CustomMultiSelect } from '../components/ui/CustomMultiSelect'
import Colors from '../utils/colors'
import Constants from '../utils/constants'
import States from '../utils/states'
import { showToast } from '../utils/toast'
import { EMPLOYEE_CONSTANTS } from '../constants/employeeConstants'
import { removeUnderScoreAndCapitalize } from '../helpers/strings'

const { Title } = Typography

const Employees = () => {
  const dispatch = useDispatch()
  const { employees, loading, totalEmployees } = useSelector((state) => state.getEmployee)
  const { loading: createLoading, error: createError, success: createSuccess } = useSelector((state) => state.createEmployee)
  const { isUpdated, loading: updateLoading, error: updateError } = useSelector((state) => state.updateEmployee)
  const { isDeleted, loading: deleteLoading, error: deleteError } = useSelector((state) => state.deleteEmployee)

  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(Constants.ITEMS_PER_PAGE)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [filters, setFilters] = useState({ contactNumber: '', username: '', email: '' })

  const [form] = Form.useForm()
  const [editForm] = Form.useForm()

  useEffect(() => {
    dispatch(getAndSearchEmployee({ page: currentPage, perPage: limit }))
  }, [dispatch])

  useEffect(() => {
    if (createSuccess) {
      showToast('Employee created successfully', 'success')
      setCreateModalVisible(false)
      form.resetFields()
      dispatch(getAndSearchEmployee({ page: 1 }))
    }

    if (createError) {
      showToast(createError, 'error')
    }

    if (isDeleted) {
      dispatch(getAndSearchEmployee({ page: currentPage }))
      dispatch({ type: EMPLOYEE_CONSTANTS.DELETE_EMPLOYEE_RESET })
      showToast('Employee deleted successfully', 'success')
    }

    if (deleteError) {
      showToast(deleteError, 'error')
    }

    if (updateError) {
      showToast(updateError, 'error')
    }

    if (isUpdated) {
      setEditModalVisible(false)
      editForm.resetFields()
      setSelectedEmployee(null)
      dispatch(getAndSearchEmployee({ page: currentPage }))
      dispatch({ type: EMPLOYEE_CONSTANTS.UPDATE_EMPLOYEE_RESET })
      showToast('Employee updated successfully', 'success')
    }
  }, [createSuccess, createError, isDeleted, isUpdated])

  const handleDelete = (employeeId) => {
    setSelectedEmployeeId(employeeId)
    setDeleteModalVisible(true)
  }

  const confirmDelete = () => {
    if (selectedEmployeeId) {
      dispatch(deleteSingleEmployee(selectedEmployeeId))
    }
    setDeleteModalVisible(false)
    setSelectedEmployeeId(null)
  }

  const cancelDelete = () => {
    setDeleteModalVisible(false)
    setSelectedEmployeeId(null)
  }

  const handleCreateSubmit = (values) => {
    let formData = new FormData()
    formData.append('name', values.name)
    formData.append('email', values.email)
    formData.append('username', values.username)
    formData.append('contactNumber', values.contactNumber || '')
    formData.append('password', values.password)
    formData.append('status', values.status || Constants.STATUS.ACTIVE)
    
    // Add arrays
    if (values.states && values.states.length > 0) {
      values.states.forEach(state => {
        formData.append('states[]', state)
      })
    }
    
    if (values.categories && values.categories.length > 0) {
      values.categories.forEach(category => {
        formData.append('categories[]', category)
      })
    }

    dispatch(createEmployee(formData))
  }

  const stateOptions = States.map(state => ({
    value: state,
    label: removeUnderScoreAndCapitalize(state)
  }))

  const categoryOptions = Object.values(Constants.MODES).map(mode => ({
    value: mode,
    label: removeUnderScoreAndCapitalize(mode)
  }))

  const handleCreateCancel = () => {
    setCreateModalVisible(false)
    form.resetFields()
  }

  const handleEdit = (employee) => {
    setSelectedEmployee(employee)
    setEditModalVisible(true)

    editForm.setFieldsValue({
      name: employee.name,
      email: employee.email,
      username: employee.username,
      contactNumber: employee.contactNumber,
      status: employee.status,
      states: employee.states,
      categories: employee.categories
    })
  }

  const handleEditSubmit = (values) => {
    let formData = new FormData()
    formData.append('name', values.name)
    formData.append('email', values.email)
    formData.append('username', values.username)
    formData.append('contactNumber', values.contactNumber || '')
    formData.append('status', values.status || Constants.STATUS.ACTIVE)
    
    if (values.states && values.states.length > 0) {
      values.states.forEach(state => {
        formData.append('states[]', state)
      })
    }
    
    if (values.categories && values.categories.length > 0) {
      values.categories.forEach(category => {
        formData.append('categories[]', category)
      })
    }

    dispatch(updateSingleEmployee(selectedEmployee._id, formData))
  }

  const handleEditCancel = () => {
    setEditModalVisible(false)
    editForm.resetFields()
    setSelectedEmployee(null)
  }

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
      width: 50
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 100
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      width: 120
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 120
    },
    {
      title: 'Phone',
      dataIndex: 'contactNumber',
      key: 'contactNumber',
      sorter: true,
      width: 100
    },
    {
      title: 'States',
      dataIndex: 'states',
      key: 'states',
      render: (states) => states?.map(state => removeUnderScoreAndCapitalize(state)).join(', ') || 'N/A',
      width: 120
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      key: 'categories',
      render: (categories) => categories?.map(category => removeUnderScoreAndCapitalize(category)).join(', ') || 'N/A',
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{
          backgroundColor: status === 'active' ? '#52c41a' : '#ff4d4f',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '500'
        }}>
          {status?.toUpperCase()}
        </span>
      ),
      width: 80
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            size="small"
            onClick={() => handleEdit(record)}
            className="edit-btn"
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
          >
            <EditOutlined /> Edit
          </CustomButton>
          <CustomButton 
            size="small"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDelete(record._id)
            }}
            className="delete-btn"
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
          >
            <DeleteOutlined /> Delete
          </CustomButton>
        </Space>
      ),
      width: 100
    }
  ]

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value }
    setFilters(newFilters)
    setCurrentPage(1)
    dispatch(getAndSearchEmployee({ page: 1, perPage: limit, ...newFilters }))
  }

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current)
    dispatch(getAndSearchEmployee({ page: pagination.current, perPage: limit, ...filters }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
          All Employees
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          className='create-btn'
        >
          Add Employee
        </Button>
      </div>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={8} lg={5} style={{ marginBottom: 16 }}>
          <CustomInput
            placeholder="Filter by contact number"
            prefix={<PhoneOutlined style={{ color: Colors.gray }} />}
            value={filters.contactNumber}
            onChange={(e) => handleFilterChange('contactNumber', e.target.value)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={5} style={{ marginBottom: 16 }}>
          <CustomInput
            placeholder="Filter by username"
            prefix={<UserOutlined style={{ color: Colors.gray }} />}
            value={filters.username}
            onChange={(e) => handleFilterChange('username', e.target.value)}
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={5} style={{ marginBottom: 16 }}>
          <CustomInput
            placeholder="Filter by email"
            prefix={<MailOutlined style={{ color: Colors.gray }} />}
            value={filters.email}
            onChange={(e) => handleFilterChange('email', e.target.value)}
          />
        </Col>
      </Row>
      
      <div style={{ width: '100%', overflowX: 'auto', border: `1px solid ${Colors.darkerGray}`, borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={employees}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: totalEmployees,
            pageSize: limit,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
            itemRender: (current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === currentPage ? Colors.primary : 'transparent',
                    color: current === currentPage ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </div>
      
      <Modal
        title="Delete Employee"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okType="danger"
        centered
        confirmLoading={deleteLoading}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>Are you sure you want to delete this employee?</span>
        </div>
      </Modal>

      <Modal
        title="Add New Employee"
        open={createModalVisible}
        onOk={() => form.submit()}
        onCancel={handleCreateCancel}
        okText="Create Employee"
        cancelText="Cancel"
        width={550}
        confirmLoading={createLoading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter employee name' }]}
              >
                <CustomInput 
                  prefix={<UserOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter employee name" 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <CustomInput 
                  prefix={<MailOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter email address" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: 'Please enter username' }]}
              >
                <CustomInput 
                  prefix={<UserOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter username" 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please enter password' }]}
              >
                <CustomInput 
                  type="password" 
                  prefix={<LockOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter password" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="contactNumber"
            label="Phone Number"
          >
            <CustomInput 
              type="number" 
              prefix={<PhoneOutlined style={{ color: Colors.gray }} />}
              placeholder="Enter phone number" 
            />
          </Form.Item>

          <Form.Item
            name="states"
            label="States"
          >
            <CustomMultiSelect
              options={stateOptions}
              placeholder="Select states"
              suffixIcon={<GlobalOutlined style={{ color: Colors.gray }} />}
            />
          </Form.Item>

          <Form.Item
            name="categories"
            label="Categories"
          >
            <CustomMultiSelect
              options={categoryOptions}
              placeholder="Select categories"
              suffixIcon={<TagsOutlined style={{ color: Colors.gray }} />}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Employee"
        open={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={handleEditCancel}
        okText="Update Employee"
        cancelText="Cancel"
        width={550}
        confirmLoading={updateLoading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter employee name' }]}
              >
                <CustomInput 
                  prefix={<UserOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter employee name" 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <CustomInput 
                  prefix={<MailOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter email address" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
              >
                <CustomInput 
                  prefix={<UserOutlined style={{ color: Colors.gray }} />}
                  placeholder="Enter username"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Select.Option value="active">Active</Select.Option>
                  <Select.Option value="inactive">Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="contactNumber"
            label="Phone Number"
          >
            <CustomInput 
              type="number" 
              prefix={<PhoneOutlined style={{ color: Colors.gray }} />}
              placeholder="Enter phone number" 
            />
          </Form.Item>

          <Form.Item
            name="states"
            label="States"
          >
            <CustomMultiSelect
              options={stateOptions}
              placeholder="Select states"
              suffixIcon={<GlobalOutlined style={{ color: Colors.gray }} />}
            />
          </Form.Item>

          <Form.Item
            name="categories"
            label="Categories"
          >
            <CustomMultiSelect
              options={categoryOptions}
              placeholder="Select categories"
              suffixIcon={<TagsOutlined style={{ color: Colors.gray }} />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Employees